import React, { useState, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';

// Fruit data
const fruits = [
  { name: 'Apple', color: 'red', img: '/image/apple.png' },
  { name: 'Banana', color: 'yellow', img: '/image/banana.png' },
  { name: 'Orange', color: 'orange', img: '/image/orange.png' },
  { name: 'Grape', color: 'purple', img: '/image/grape.png' },
  { name: 'Lime', color: 'green', img: '/image/lime.png' }
];

const baskets = [
  { label: 'Red Basket', color: 'red' },
  { label: 'Yellow Basket', color: 'yellow' },
  { label: 'Orange Basket', color: 'orange' },
  { label: 'Purple Basket', color: 'purple' },
  { label: 'Green Basket', color: 'green' }
];

export const FruitBasketSortGame = ({ handleClose, onGameComplete }) => {
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(1);
  const [fruitChoices, setFruitChoices] = useState([]);
  const [targetFruit, setTargetFruit] = useState(null);
  const [targetBasket, setTargetBasket] = useState(null);
  const [celebrated, setCelebrated] = useState(false);
  const [fruitStatus, setFruitStatus] = useState({});
  const [animateFruit, setAnimateFruit] = useState('');
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);

  const correctSound = useRef(null);
  const errorSound = useRef(null);

  // Load sounds and volume
  useEffect(() => {
    correctSound.current = new Audio('/sounds/correct.mp3');
    errorSound.current = new Audio('/sounds/error.mp3');
    const savedVolume = localStorage.getItem('tapGameVolume');
    if (savedVolume !== null) setVolume(parseFloat(savedVolume));
  }, []);

  // Setup next round
  const nextRound = () => {
    const shuffledFruits = fruits.sort(() => 0.5 - Math.random()).slice(0, 3);
    const answerFruit = shuffledFruits[Math.floor(Math.random() * shuffledFruits.length)];
    const answerBasket = baskets.find(b => b.color === answerFruit.color);
    setFruitChoices(shuffledFruits);
    setTargetFruit(answerFruit);
    setTargetBasket(answerBasket);
    setFruitStatus({});
    setAnimateFruit('');
    setTimeout(() => speakTarget(answerFruit, answerBasket), 400);
  };

  useEffect(() => {
    nextRound();
    // eslint-disable-next-line
  }, [round]);

  // Celebrate and handleClose after 10 rounds
  useEffect(() => {
    if (score === 10 && !celebrated) {
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
      setCelebrated(true);
      // Was missing entirely - the parent's onGameComplete prop (which
      // saves progress via GAME_SAVE, marks the level completed, and
      // shows the "Level Completed!" dialog) was never called here, so
      // finishing this game silently lost the student's progress.
      onGameComplete?.(3, score * 10);
      setTimeout(() => handleClose?.(), 2000);
    }
  }, [score, celebrated, handleClose, onGameComplete]);

  // Speak target
  useEffect(() => {
    if (targetFruit && targetBasket && !isMuted) {
      const speakTimeout = setTimeout(() => {
        window.speechSynthesis.cancel();
        const utter = new window.SpeechSynthesisUtterance(
          `Put the ${targetFruit.name} in the ${targetBasket.label}`
        );
        utter.rate = 0.9;
        utter.volume = volume;
        window.speechSynthesis.speak(utter);
      }, 600);
      return () => clearTimeout(speakTimeout);
    }
  }, [targetFruit, targetBasket, isMuted, volume]);

  const speakTarget = (fruit, basket) => {
    if (isMuted || !fruit || !basket) return;
    window.speechSynthesis.cancel();
    const utter = new window.SpeechSynthesisUtterance(
      `Put the ${fruit.name} in the ${basket.label}`
    );
    utter.rate = 0.9;
    utter.volume = volume;
    window.speechSynthesis.speak(utter);
  };

  const handleClick = (fruit) => {
    if (fruit.color === targetBasket.color) {
      if (!isMuted && correctSound.current) {
        correctSound.current.volume = volume;
        correctSound.current.play();
        const utter = new SpeechSynthesisUtterance('Correct!');
        utter.volume = volume;
        window.speechSynthesis.speak(utter);
      }
      setAnimateFruit(fruit.name);
      setFruitStatus(prev => ({ ...prev, [fruit.name]: 'correct' }));
      setTimeout(() => setAnimateFruit(''), 500);
      setScore(prev => prev + 1);
      setTimeout(() => setRound(r => r + 1), 400);
    } else {
      if (!isMuted && errorSound.current) {
        errorSound.current.volume = volume;
        errorSound.current.play();
        const utter = new SpeechSynthesisUtterance('Try again!');
        utter.volume = volume;
        window.speechSynthesis.speak(utter);
      }
      setFruitStatus(prev => ({ ...prev, [fruit.name]: 'wrong' }));
      setAnimateFruit(fruit.name);
      setTimeout(() => setFruitStatus(prev => ({ ...prev, [fruit.name]: null })), 700);
      setTimeout(() => setAnimateFruit(''), 500);
    }
  };

  return (
    <div className="tap-letter-board-bg">
      <div className="tap-header-bar mb-1">
        <div className="tap-score-box">
          <span role="img" aria-label="score" style={{ fontSize: 22, marginRight: 6 }}>🏆</span>
          <span className="tap-score-label">Score</span>
          <span className="tap-score-value">{score}/10</span>
        </div>
        <div className="tap-letter-target">
          <span className="tap-letter-label">Put the:</span>
          <span className="tap-letter-value">
            {targetFruit && targetBasket && (
              <>
                <span style={{ color: targetFruit.color }}>{targetFruit.name}</span>
                <span> in the </span>
                <span style={{ color: targetBasket.color }}>{targetBasket.label}</span>
              </>
            )}
          </span>
        </div>
        <div className="tap-volume-box">
          <span role="img" aria-label="volume" style={{ fontSize: 20, marginRight: 4 }}>🔊</span>
          <input
            id="volume"
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => {
              const newVol = parseFloat(e.target.value);
              setVolume(newVol);
              localStorage.setItem('tapGameVolume', newVol);
            }}
            style={{ width: '90px', verticalAlign: 'middle' }}
          />
          <span className="tap-volume-value">{Math.round(volume * 100)}%</span>
          <button
            className="btn btn-sm btn-outline-secondary ms-2"
            style={{ borderRadius: 12, padding: '2px 8px', marginLeft: 8 }}
            title="Replay"
            onClick={() => {
              if (targetFruit && targetBasket && !isMuted) {
                const utter = new window.SpeechSynthesisUtterance(
                  `Put the ${targetFruit.name} in the ${targetBasket.label}`
                );
                utter.rate = 0.9;
                utter.volume = volume;
                window.speechSynthesis.speak(utter);
              }
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 20, verticalAlign: 'middle' }}>replay</span>
          </button>
        </div>
      </div>

      <div className="tap-letter-btn-grid" style={{ minHeight: 180 }}>
        {fruitChoices.map(fruit => (
          <button
            key={fruit.name}
            className={`btn m-1 letter-btn
              ${animateFruit === fruit.name ? 'pulse' : ''}
              ${fruitStatus[fruit.name] === 'correct' ? 'btn-success text-white selected-correct' : ''}
              ${fruitStatus[fruit.name] === 'wrong' ? 'btn-danger text-white shake' : ''}
              ${!fruitStatus[fruit.name] ? 'btn-outline-primary' : ''}
            `}
            onClick={() => handleClick(fruit)}
            disabled={score === 10}
            style={{
              width: '100px',
              height: '110px',
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <img
              src={fruit.img}
              alt={fruit.name}
              style={{ width: '70px', height: '70px', objectFit: 'contain', marginBottom: 8 }}
            />
            <span style={{ fontWeight: 600 }}>{fruit.name}</span>
          </button>
        ))}
      </div>

      <div className="d-flex flex-wrap justify-content-center" style={{ gap: 20, marginBottom: 10 }}>
        {targetBasket && (
          <div
            style={{
              width: 140,
              height: 120,
              borderRadius: 24,
              background: '#fffbe7',
              boxShadow: `0 4px 16px ${targetBasket.color}44`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-end',
              position: 'relative',
              border: `4px solid ${targetBasket.color}`
            }}
          >
            <img
              src="/image/basket.png"
              alt="Basket"
              style={{
                width: 100,
                height: 80,
                objectFit: 'contain',
                marginBottom: 0,
                filter: `drop-shadow(0 2px 8px ${targetBasket.color}66)`
              }}
            />
            <span
              style={{
                position: 'absolute',
                top: 8,
                left: '50%',
                transform: 'translateX(-50%)',
                background: targetBasket.color,
                color: '#fff',
                padding: '4px 16px',
                borderRadius: 12,
                fontWeight: 700,
                fontSize: 16,
                boxShadow: '0 2px 8px #0002'
              }}
            >
              {targetBasket.label}
            </span>
          </div>
        )}
      </div>

      {score === 10 && <h4 className="text-success mt-3">🎉 Well done! All rounds completed.</h4>}

      <style>
        {`
          .tap-letter-board-bg {
            background: url('/image/board.png'), linear-gradient(135deg,rgba(62, 62, 62, 0.3) 0%,rgba(15, 15, 14, 0.41) 100%);
            background-size: cover, cover;
            background-position: center, center;
            border-radius: 32px;
            box-shadow: 0 6px 32px #e0c68a33;
            padding: 32px 12px 24px 12px;
            margin: 0 auto;
            max-width: 900px;
            width: 98%;
            min-height: 400px;
            display: flex;
            flex-direction: column;
            align-items: center;
            position: relative;
            z-index: 2;
            animation: board-pop 0.5s cubic-bezier(.68,-0.55,.27,1.55);
          }
          @keyframes board-pop {
            0% { transform: scale(0.9); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
          .tap-header-bar {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-radius: 20px;
            box-shadow: 0 2px 8px rgba(224,198,138,0.10);
            padding: 1px 18px 18px 18px;
            margin-bottom: 18px;
            gap: 0;
          }
          .tap-score-box,
          .tap-volume-box {
            display: flex;
            align-items: center;
            border-radius: 14px;
            padding: 6px 14px;
            font-weight: 700;
            font-size: 1.1rem;
            color: #b8860b;
            background: rgba(255, 255, 255, 0.86);
            min-width: 110px;
            box-shadow: 0 1px 4px rgba(224,198,138,0.10);
          }
          .tap-score-label {
            margin-right: 6px;
            font-size: 1rem;
            color: #b8860b;
          }
          .tap-score-value {
            font-size: 1.1rem;
            color: #1976d2;
            margin-left: 4px;
            font-weight: 700;
          }
          .tap-letter-target {
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(224,198,138,0.92);
            border-radius: 18px;
            padding: 8px 32px;
            box-shadow: 0 1px 4px rgba(224,198,138,0.10);
            margin: 0 18px;
            min-width: 180px;
          }
          .tap-letter-label {
            font-size: 1.1rem;
            color: #3e2723;
            margin-right: 8px;
            font-weight: 600;
          }
          .tap-letter-value {
            font-size: 2rem;
            font-weight: 900;
            letter-spacing: 2px;
            text-shadow: 1px 1px 8px #fff, 0 0 8px #e0c68a;
          }
          .tap-volume-value {
            margin-left: 8px;
            font-size: 1rem;
            color: #1976d2;
            font-weight: 700;
            min-width: 36px;
            display: inline-block;
            text-align: right;
          }
          .tap-letter-btn-grid {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            align-items: center;
            width: 100%;
            min-height: 180px;
            margin-top: 10px;
            gap: 8px;
          }
          .letter-btn {
            padding: 0;
            border-radius: 18px !important;
            background: linear-gradient(135deg,rgb(250, 248, 240) 0%,rgb(248, 228, 166) 100%) !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.10);
            border: 2px solid rgba(223, 214, 192, 0.65) !important;
            min-width: 90px;
            min-height: 60px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: transform 0.1s, box-shadow 0.2s;
          }
          .letter-btn:active, .letter-btn:focus {
            transform: scale(0.96);
            box-shadow: 0 0 0 3px #ffe08255;
          }
          .pulse {
            animation: pulse 0.5s;
          }
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.12); }
            100% { transform: scale(1); }
          }
          .shake {
            animation: shake 0.4s;
          }
          @keyframes shake {
            0% { transform: translateX(0); }
            20% { transform: translateX(-8px); }
            40% { transform: translateX(8px); }
            60% { transform: translateX(-8px); }
            80% { transform: translateX(8px); }
            100% { transform: translateX(0); }
          }
          .selected-correct,
          .btn-success.selected-correct {
            background: #000000 !important;
            border-color:rgb(0, 245, 253) !important;
            color: #fff !important;
            box-shadow: 0 0 16px 2px rgba(122, 255, 244, 0.6);
            animation: correct-pop 0.5s;
            transparent: none !important;
          }
          @keyframes correct-pop {
            0% { transform: scale(1.15); }
            100% { transform: scale(1); }
          }
          /* Responsive styles */
          @media (max-width: 1024px) {
            .tap-letter-board-bg {
              max-width: 99vw;
              padding: 18px 2vw 18px 2vw;
              min-height: 260px;
            }
            .tap-letter-btn-grid {
              min-height: 120px;
            }
            .letter-btn {
              min-width: 70px;
              min-height: 48px;
            }
          }
          @media (max-width: 700px) {
            .tap-header-bar {
              flex-direction: column;
              gap: 10px;
              padding-bottom: 10px;
            }
            .tap-letter-target {
              min-width: 0;
              width: 100%;
              padding: 8px 0;
              margin: 8px 0;
            }
            .tap-letter-board-bg {
              padding: 10px 2vw 10px 2vw;
              min-height: 180px;
            }
            .tap-letter-btn-grid {
              min-height: 80px;
              gap: 4px;
            }
            .letter-btn {
              min-width: 48px;
              min-height: 32px;
            }
          }
          @media (max-width: 480px) {
            .tap-letter-board-bg {
              padding: 6px 1vw 6px 1vw;
              min-height: 120px;
            }
            .tap-header-bar {
              padding: 6px 4px 6px 4px;
            }
            .tap-letter-btn-grid {
              gap: 2px;
            }
            .letter-btn {
              min-width: 36px;
              min-height: 24px;
            }
          }
        `}
      </style>
    </div>
  );
};